import React, { Component } from 'react';
import dynamic from 'next/dynamic';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import memoize from 'lodash/memoize';

// import TextTopic from '~/components/Row/Topics/TextTopic';
// import ImageAndTextTopic from '~/components/Row/Topics/ImageAndTextTopic';
// import SeperatorTopic from '~/components/Row/Topics/SeperatorTopic';
// import SliderTopic from '~/components/Topic/SliderTopic';
// import CardTopic from '~/components/Topic/CardTopic';
// import ImageTopic from '~/components/Topic/ImageTopic';
// import SquareCard from '~/components/Card/SquareCard';
// import FourThirdCard from '~/components/Card/FourThirdCard';
// import SouvenirCard from '~/components/Card/SouvenirCard';
// import TicketCard from '~/components/Card/TicketCard';
// import CarCard from '~/components/Card/CarCard';
// import ArticleCard from '~/components/Card/ArticleCard';
// import LxCard from '~/components/Card/LxCard';
// import BreakpointTopic from '~/components/Row/Topics/BreakpointTopic';
// import TopicFilterMenu from '~/components/Filter/TopicFilterMenu';
import { getCity } from '~/utils/helpers';
import { languagesList, countryMapLanguage } from '~/utils/topic';

// const Element = Scroll.Element;
moment.tz.setDefault('Asia/Bangkok');

const SliderTopic = dynamic(() => import(/* webpackChunkName: "Topics.SliderTopic.components" */'./SliderTopic'), { loading: () => null });
const CardTopic = dynamic(() => import(/* webpackChunkName: "Topics.CardTopic.components" */'./CardTopic'), { loading: () => null });

const TextTopic = dynamic(() => import(/* webpackChunkName: "Topics.TextTopic.components" */'../Row/Topics/TextTopic'), { loading: () => null });
const ImageAndTextTopic = dynamic(() => import(/* webpackChunkName: "Topics.ImageAndTextTopic.components" */'../Row/Topics/ImageAndTextTopic'));
const ImageTopic = dynamic(() => import(/* webpackChunkName: "Topics.ImageTopic.components" */'./ImageTopic'), { loading: () => null });
const SeperatorTopic = dynamic(() => import(/* webpackChunkName: "Topics.SeperatorTopic.components" */'../Row/Topics/SeperatorTopic'), { loading: () => null });
const BreakpointTopic = dynamic(() => import(/* webpackChunkName: "Topics.BreakpointTopic.components" */'../Row/Topics/BreakpointTopic'), { loading: () => null });
const QuizTopic = dynamic(() => import(/* webpackChunkName: "Topics.QuizTopic.components" */'./QuizTopic'), { loading: () => null });
const PromoTopic = dynamic(() => import(/* webpackChunkName: "Topics.PromoTopic.components" */'../Row/Topics/PromoTopic'), { loading: () => null });

const FourThirdCard = dynamic(() => import(/* webpackChunkName: "Card.FourThirdCard.components" */'../Card/FourThirdCard'), { loading: () => null });
const SquareCard = dynamic(() => import(/* webpackChunkName: "Card.SquareCard.components" */'../Card/SquareCard'), { loading: () => null });
const SouvenirCard = dynamic(() => import(/* webpackChunkName: "Card.SouvenirCard.components" */'../Card/SouvenirCard'), { loading: () => null });
const TicketCard = dynamic(() => import(/* webpackChunkName: "Card.TicketCard.components" */'../Card/TicketCard'), { loading: () => null });
const CarCard = dynamic(() => import(/* webpackChunkName: "Card.CarCard.components" */'../Card/CarCard'), { loading: () => null });
const ArticleCard = dynamic(() => import(/* webpackChunkName: "Card.CarCard.components" */'../Card/ArticleCard'), { loading: () => null });
const LxCard = dynamic(() => import(/* webpackChunkName: "Card.CarCard.components" */'../Card/LxCard'), { loading: () => null });

const TopicFilterMenu = dynamic(() => import(/* webpackChunkName: "Topics.TopicFilterMenu.components" */'../Filter/TopicFilterMenu'), { loading: () => null });

const topicTypes = {
  text: TextTopic,
  imageAndText: ImageAndTextTopic,
  image: ImageTopic,
  seperator: SeperatorTopic,
  slider: SliderTopic,
  card: CardTopic,
  breakpoint: BreakpointTopic,
  quiz: QuizTopic,
  promo: PromoTopic,
};

const getSeeMore = memoize(
  (topic, url) => {
    const { link } = topic;
    const city = getCity(url.query.city);
    if (link && link.type && link.type !== '') {
      if (link.type === 'city') {
        return `/to/${link.value}`;
      }
      if (link.type === 'type') {
        return `/to/${city}/${link.value}`;
      }
      if (link.type === 'custom') {
        return link.value;
      }
      return null;
    }
    return null;
  },
  (topic, url) => {
    const { link } = topic;
    return `${url.query.city ? url.query.city : 'n/a'}-${link && link.value ? link.value : 'n/a'}`;
  }
);

const getTopicData = (topic, showAll, url) => {
  let type = '';
  const options = {
    fullPath: url.query.fullPath
  };
  if (['topics', 'trips', 'users', 'availableTrips', 'souvenirs', 'tickets', 'car_rental', 'articles', 'recommendationTrips', 'filterTrips'].indexOf(topic.contains) >= 0) {
    if (showAll || topic.contains === 'articles' || topic.alignment === 'vertical') {
      type = 'card';
    } else {
      type = 'slider';
    }

    // TODO: IMPROVE THIS
    if (topic.contains === 'topics') {
      options.objects = topic.topic_ids;
      options.component = SquareCard;
      options.desktopColumn = '1-4';
      options.mobileColumn = '42p';
    }
    if (['trips', 'availableTrips', 'recommendationTrips', 'filterTrips', 'tickets'].includes(topic.contains)) {
      options.objects = topic.trip_ids;
      options.alignment = topic.alignment || '';
      options.component = props => (
        topic.cardType
          ? <FourThirdCard {...props} displayType={topic.cardType} />
          : <FourThirdCard {...props} />
      );
      options.desktopColumn = '1-4';
      options.tabletColumn = '37p';
      options.mobileColumn = '77p';
    }
    if (topic.contains === 'souvenirs') {
      options.objects = topic.trip_ids;
      options.component = SouvenirCard;
      options.desktopColumn = '1-4';
      options.mobileColumn = '65p';
    }
    if (topic.contains === 'tickets') {
      options.objects = topic.trip_ids;
      options.component = TicketCard;
      options.desktopColumn = '1-4';
      options.tabletColumn = '37p';
      options.mobileColumn = '77p';
    }
    if (topic.contains === 'car_rental') {
      options.objects = topic.trip_ids;
      options.component = CarCard;
      options.desktopColumn = '1-4';
      options.tabletColumn = '37p';
      options.mobileColumn = '77p';
    }
    if (topic.contains === 'users') {
      options.objects = topic.user_ids;
      options.component = LxCard;
      options.desktopColumn = '1-6';
      options.tabletColumn = '23p';
      options.mobileColumn = '47p';
    }
    if (topic.contains === 'articles') {
      options.objects = topic.articles;
      options.component = ArticleCard;
      options.desktopColumn = '1-3';
      options.mobileColumn = '65p';
    }
    if (['topics', 'trips', 'availableTrips', 'recommendationTrips', 'filterTrips', 'tickets'].includes(topic.contains)) {
      options.scrollSize = 4;
    } else {
      options.scrollSize = 3;
    }
    options.seeMore = getSeeMore(topic, url);
    options.isOmitTitle = topic.omit_title;
  } else if (topic.contains === 'static-content') {
    const topicType = topic.data.type;
    type = topicType;
  } else {
    type = topic.contains;
  }
  return {
    C: topicTypes[type],
    options
  };
};
// , (topic, showAll, url, userType) => {
//   let key = `${topic._id}-${topic.contains}-${topic.index || 'na-index'}-${!!showAll}-${url.query.city || ''}`;
//   if (topic.contains === 'availableTrips') {
//     key += `${topic.trip_ids.map(t => t._id).join(',')}`;
//   }
//   if (topic.contains === 'recommendationTrips') {
//     key += `${userType ? userType.type : 'na-lang'}`;
//   }
//   if (topic.contains === 'users' && topic.is_filter_by_lang) {
//     key += `-filter-by-lang-${userType ? userType.type : 'na-lang'}`;
//   }
//   if (topic.contains === 'filterTrips') {
//     key += `${topic.trip_ids.map(t => t._id).join(',')}`;
//   }
//   return key;
// });

class Topic extends Component {
  static propTypes = {
    logImpressTopic: PropTypes.func,
    // display: PropTypes.number,
    // sessionData: PropTypes.object,
    options: PropTypes.object,
    topic: PropTypes.object,
    url: PropTypes.object,
    showAll: PropTypes.bool,
    index: PropTypes.number,
    sessionData: PropTypes.object,
  };

  static topicTypes = {
    text: TextTopic,
    imageAndText: ImageAndTextTopic,
    image: ImageTopic,
    seperator: SeperatorTopic,
    slider: SliderTopic,
    card: CardTopic,
    breakpoint: BreakpointTopic,
    quiz: QuizTopic
  };

  constructor(props) {
    super(props);
    this.topicImpressed = false;
    // this.getTopicData = this.getTopicData.bind(this);
    // this.getSeeMore = this.getSeeMore.bind(this);
  }

  componentDidMount() {
    if (this.root) {
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !this.topicImpressed) {
          if (this.props.logImpressTopic) {
            this.props.logImpressTopic({
              name: this.props.topic.name,
              type: this.props.topic.contains,
              topicId: this.props.topic._id,
              index: this.props.index
            });
          }
          this.topicImpressed = true;
          this.observer.unobserve(this.root);
        }
      }, { threshold: [0.67] });
      this.observer.observe(this.root);
    }
  }

  shouldComponentUpdate(nextProps) {
    if (this.props.topic._id !== nextProps.topic._id) {
      return true;
    }
    if (!this.props.sessionData && nextProps.sessionData) {
      return true;
    }
    if (!this.props.sessionData && nextProps.sessionData) {
      return true;
    }
    if (this.props.sessionData && nextProps.sessionData && this.props.sessionData.quantity !== nextProps.sessionData.quantity) {
      return true;
    }
    if (
      this.props.topic.contains === 'availableTrips'
      && nextProps.topic.contains === 'availableTrips'
      && !moment(this.props.sessionData.date).isSame(moment(nextProps.sessionData.date), 'day')
    ) {
      return true;
    }
    if (
      this.props.topic.contains === 'filterTrips' && nextProps.topic.contains === 'filterTrips'
    ) {
      return true;
    }
    if (
      this.props.topic.contains === 'recommendationTrips'
      && nextProps.topic.contains === 'recommendationTrips'
    ) {
      // if (
      //   this.props.sessionData.userType
      //   && nextProps.sessionData.userType
      //   && this.props.sessionData.userType.type !== nextProps.sessionData.userType.type
      // ) {
      //   return true;
      // }
      // if (
      //   this.props.sessionData.userType === undefined
      //   && nextProps.sessionData
      // ) {
      //   return true;
      // }
      return true;
    }
    if (
      this.props.topic.contains === 'static-content'
      && nextProps.topic.contains === 'static-content'
    ) {
      return true;
    }
    if (
      this.props.topic.contains === 'users'
      && nextProps.topic.contains === 'users'
      && this.props.topic.is_filter_by_lang
      && nextProps.topic.is_filter_by_lang
    ) {
      if (
        this.props.sessionData.userType
        && nextProps.sessionData.userType
        && this.props.sessionData.userType.type !== nextProps.sessionData.userType.type
      ) {
        return true;
      }
      if (
        this.props.sessionData.userType === undefined
        && nextProps.sessionData
      ) {
        return true;
      }
    }
    // // TO DO: change from name to some other type
    // if (this.props.topic.name === 'Recently Viewed') {
    //   return (!this.props.topic.trip_ids && !!nextProps.topic.trip_ids);
    // }
    return false;
  }

  componentWillUnmount() {
    // if (this.interval) {
    //   clearInterval(this.interval);
    // }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // getSeeMore(topic) {
  //   const { url } = this.props;
  //   const { link } = topic;
  //   const city = getCity(url.query.city);
  //   if (link && link.type && link.type !== '') {
  //     if (link.type === 'city') {
  //       return `/to/${link.value}`;
  //     }
  //     if (link.type === 'type') {
  //       return `/to/${city}/${link.value}`;
  //     }
  //     if (link.type === 'custom') {
  //       return link.value;
  //     }
  //     return null;
  //   }
  //   return null;
  // }

  // getTopicData(topic) {
  //   let type = '';
  //   const options = {
  //     fullPath: this.props.url.query.fullPath
  //   };
  //   if (['topics', 'trips', 'users', 'availableTrips', 'souvenirs', 'tickets', 'car_rental', 'articles', 'recommendationTrips', 'filterTrips'].indexOf(topic.contains) >= 0) {
  //     if (this.props.showAll || topic.contains === 'articles') {
  //       type = 'card';
  //     } else {
  //       type = 'slider';
  //     }

  //     // TODO: IMPROVE THIS
  //     if (topwindowic.contains === 'topics') {
  //       options.objects = topic.topic_ids;
  //       options.component = SquareCard;
  //       options.desktopColumn = '1-4';
  //       options.mobileColumn = '42p';
  //     }
  //     if (topic.contains === 'trips' || topic.contains === 'availableTrips' || topic.contains === 'recommendationTrips' || topic.contains === 'filterTrips') {
  //       options.objects = topic.trip_ids;
  //       options.component = FourThirdCard;
  //       options.desktopColumn = '1-4';
  //       options.tabletColumn = '37p';
  //       options.mobileColumn = '77p';
  //     }
  //     if (topic.contains === 'souvenirs') {
  //       options.objects = topic.trip_ids;
  //       options.component = SouvenirCard;
  //       options.desktopColumn = '1-4';
  //       options.mobileColumn = '65p';
  //     }
  //     if (topic.contains === 'tickets') {
  //       options.objects = topic.trip_ids;
  //       options.component = TicketCard;
  //       options.desktopColumn = '1-4';
  //       options.tabletColumn = '37p';
  //       options.mobileColumn = '77p';
  //     }
  //     if (topic.contains === 'car_rental') {
  //       options.objects = topic.trip_ids;
  //       options.component = CarCard;
  //       options.desktopColumn = '1-4';
  //       options.tabletColumn = '37p';
  //       options.mobileColumn = '77p';
  //     }
  //     if (topic.contains === 'users') {
  //       options.objects = topic.user_ids;
  //       options.component = LxCard;
  //       options.desktopColumn = '1-6';
  //       options.tabletColumn = '23p';
  //       options.mobileColumn = '47p';
  //     }
  //     if (topic.contains === 'articles') {
  //       options.objects = topic.articles;
  //       options.component = ArticleCard;
  //       options.desktopColumn = '1-3';
  //       options.mobileColumn = '65p';
  //     }
  //     if (['topics', 'trips', 'availableTrips', 'recommendationTrips', 'filterTrips', 'tickets'].includes(topic.contains)) {
  //       options.scrollSize = 4;
  //     } else {
  //       options.scrollSize = 3;
  //     }
  //     options.seeMore = this.getSeeMore(topic);
  //     options.isOmitTitle = topic.omit_title;
  //   } else {
  //     type = topic.contains;
  //   }
  //   return {
  //     C: Topic.topicTypes[type],
  //     options
  //   };
  // }
  render() {
    const { topic, sessionData } = this.props;
    const { C, options } = getTopicData(
      topic,
      this.props.showAll,
      this.props.url,
      sessionData ? sessionData.userType : null
    );
    if (!C) return null;
    if (topic.contains === 'users' && topic.is_filter_by_lang) {
      const selectedCountry = (sessionData.userType && sessionData.userType.type) || '';
      const isInSelectedCountryList = selectedCountry === 'Thailand' ? true : languagesList.includes(countryMapLanguage[selectedCountry]) || false;
      if (!isInSelectedCountryList) return null;
    } else if (topic.contains === 'filterTrips') {
      return (
        <TopicFilterMenu
          {...this.props}
          omitTitle={topic.omit_title}
          sliderRender={() => (
            <div ref={(c) => { this.root = c; }}>
              <C {...topic} {...this.props} {...options} {...this.props.options} />
            </div>
          )}
        />
      );
    }

    return (
      <div ref={(c) => { this.root = c; }} key={topic._id}>
        <section id={topic.slug || undefined} style={{ position: 'relative', top: '-70px' }} />
        <C {...topic} {...this.props} {...options} {...this.props.options} />
      </div>
    );
  }
}

export default Topic;
